import React, { useEffect, useRef, useState } from 'react';
import { bool, arrayOf, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes, REVIEW_TYPE_OF_CUSTOMER, REVIEW_TYPE_OF_PROVIDER } from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { withViewport } from '../../util/uiHelpers';
import { isScrollingDisabled, setDOMLoaded, setFirstLoaded } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import CompactListingCard from '../../components/ListingCard/CompactListingCard';
import { addFavorite, removeFavorite, getFavoriteListings } from '../../ducks/favorites.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import {
  Page,
  LayoutSingleColumn,
  IconWeb,
  IconPhone,
  IconLocation,
  ReviewRating,
  InlineTextButton,
  Avatar,
  AvatarLarge,
  OptimizedImage,
  NamedLink,
  LoginPromptModal,
} from '../../components';

import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './TeamPage.module.css';
import { ensureProtocol, hasQueryParam, redirectToStore, createSlug } from '../../util/urlHelpers';
import { useHistory } from 'react-router-dom';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { fetchCreators, fetchReviewers } from '../../ducks/user.duck';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { ASSOCIATED, OWNER } from '../../util/listingsHelpers';
import { prepareMemberData } from '../../util/userHelpers';
import { prepareAbbreviatedName } from '../../util/data';
import { formatMoney } from '../../util/currency';
import { getBreadcrumb, listingIsDraft, getCreatorUser } from '../../util/listingsHelpers';
import SearchPageWithGrid from '../SearchPage/SearchPageWithGrid';
import { BRANDS } from '../../config/brandRegistry';
import { getStoreImageUrl } from '../../util/sellerImages';
import { buildYoutubeEmbedUrl, extractYoutubeVideoId } from '../../util/youtube';

const _ = require('lodash');

const MAX_MOBILE_SCREEN_WIDTH = 768;
// Mock review data is kept as design/reference material only.
// Production rendering should use real reviews or the empty state.
const ENABLE_MOCK_REVIEWS_PREVIEW = false;

const normalizeLocationValue = value => (typeof value === 'string' ? value.trim() : '');

const stripZipFromState = value =>
  normalizeLocationValue(value)
    .replace(/\s+\d{5}(?:-\d{4})?$/, '')
    .trim();

const formatCityState = (city, state) => {
  const normalizedCity = normalizeLocationValue(city);
  const normalizedState = stripZipFromState(state);

  if (normalizedCity && normalizedState) {
    return `${normalizedCity}, ${normalizedState}`;
  }

  return normalizedCity || normalizedState || null;
};

const parseCityStateFromPlaceName = placeName => {
  const parts = normalizeLocationValue(placeName)
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  if (parts.length >= 4) {
    return formatCityState(parts[1], parts[2]);
  }

  if (parts.length >= 2) {
    return formatCityState(parts[0], parts[1]);
  }

  return null;
};

const resolveHeaderCityState = publicData => {
  const canonicalAddress = publicData?.address;
  const canonicalLocation = formatCityState(canonicalAddress?.city, canonicalAddress?.state);
  if (canonicalLocation) {
    return canonicalLocation;
  }

  const legacyProfileLocation = formatCityState(publicData?.city, publicData?.state);
  if (legacyProfileLocation) {
    return legacyProfileLocation;
  }

  return parseCityStateFromPlaceName(publicData?.storedAddress?.place_name);
};

// About section resolution: personal bio takes precedence; stores fall back to the
// business description. Empty result hides the section entirely (no placeholder).
// Candidates are trimmed before the fallback runs, so a whitespace-only bio does not
// mask a usable business description.
// `profileBio` comes from TeamPage state, not from `profileUser`: the embedded search
// merges the same seller without `profile.bio` and wipes it from the entity store.
export const resolveAboutText = ({ isStore, isOwner, profileUser, associatedUser, profileBio }) => {
  const asText = value => (typeof value === 'string' && value.trim() ? value.trim() : null);

  const profile = profileUser?.attributes?.profile || {};
  const bio = asText(profileBio);

  if (isStore) {
    return bio || asText(profile.publicData?.businessProfileDescription);
  }
  if (isOwner) {
    return bio;
  }
  return asText(associatedUser?.attributes?.profile?.bio);
};

const mockReviewAuthor = (id, displayName, city, state) => ({
  id: { uuid: `mock-review-author-${id}` },
  attributes: {
    profile: {
      displayName,
      abbreviatedName: displayName
        .split(' ')
        .map(part => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase(),
      publicData: {
        city,
        state,
      },
    },
    profileImage: null,
  },
});

const mockReviewsPreview = [
  {
    id: { uuid: 'mock-review-1' },
    attributes: {
      rating: 5,
      content:
        'Great shop to work with. Communication was clear, shipping was fast, and the product arrived exactly as described.',
      createdAt: new Date('2025-07-23T12:00:00.000Z'),
      author: mockReviewAuthor(1, 'Lisa Schmidt', 'Wausau', 'Wisconsin'),
    },
  },
  {
    id: { uuid: 'mock-review-2' },
    attributes: {
      rating: 4,
      content:
        'Very helpful staff and a smooth buying experience overall. I would definitely purchase from this dealer again.',
      createdAt: new Date('2025-08-04T12:00:00.000Z'),
      author: mockReviewAuthor(2, 'Brian Matthews', 'Bozeman', 'Montana'),
    },
  },
  {
    id: { uuid: 'mock-review-3' },
    attributes: {
      rating: 5,
      content:
        'Excellent service from start to finish. The team answered my questions quickly and the order was packed with care.',
      createdAt: new Date('2025-08-18T12:00:00.000Z'),
      author: mockReviewAuthor(3, 'Courtney Hale', 'Fairbanks', 'Alaska'),
    },
  },
];

const CompactReviewCard = ({ review, intl }) => {
  const { rating, content, createdAt, author } = review.attributes || {};
  const authorName = author?.attributes?.profile?.displayName || 'Unknown User';
  const authorLocation = author?.attributes?.profile?.publicData?.city
    ? `${author.attributes.profile.publicData.city}, ${author.attributes.profile.publicData.state ||
        ''}`
    : '';

  // Use the built-in formatDate function from reactIntl directly or with proximity
  const formattedDate =
    createdAt && intl
      ? intl.formatDate(createdAt, { month: 'short', day: 'numeric', year: 'numeric' })
      : '';

  return (
    <div className={css.compactReviewCard}>
      <div className={css.compactReviewHeader}>
        <div className={css.compactReviewAuthorInfo}>
          <div className={css.compactReviewAvatar}>
            <Avatar user={author} disableProfileLink />
          </div>
          <div className={css.compactReviewAuthorDetails}>
            <span className={css.compactReviewAuthorName}>{authorName}</span>
            {authorLocation && (
              <span className={css.compactReviewAuthorLocation}>{authorLocation}</span>
            )}
          </div>
        </div>
        <div className={css.compactReviewRatingInfo}>
          <ReviewRating rating={rating} reviewStarClassName={css.compactReviewStar} />
          {formattedDate && <span className={css.compactReviewDate}>{formattedDate}</span>}
        </div>
      </div>
      <p className={css.compactReviewContent}>{content}</p>
    </div>
  );
};

export const MainContent = props => {
  const {
    userShowError,
    listings,
    queryListingsError,
    viewport,
    fetchingListings,
    profileUser,
    profileBio,
    creators,
    reviews,
    storeType,
    currentUser,
    favoriteListings,
    onAddFavorite,
    onRemoveFavorite,
    onManageDisableScrolling,
    intl,
  } = props;

  const isStore = storeType === 'store';
  const isOwner = storeType === 'owner';
  const isAssociated = storeType === 'member';

  let reviewsItems = reviews.filter(
    r => !r?.attributes?.type || r.attributes.type === REVIEW_TYPE_OF_PROVIDER
  );
  const displayedReviews =
    reviewsItems && reviewsItems.length > 0
      ? reviewsItems
      : ENABLE_MOCK_REVIEWS_PREVIEW
      ? mockReviewsPreview
      : [];

  const hasListings = listings.length > 0;
  const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isBannerMuted, setIsBannerMuted] = useState(true);
  const bannerVideoRef = useRef(null);

  const storeImageUrl = getStoreImageUrl(profileUser);

  if (userShowError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  const inlineListener = (event, name, params) => {
    event.preventDefault();
    event.stopPropagation();
    let uri = createResourceLocatorString(name, routeConfiguration, params, {});
    history.push(uri);
  };

  const handleListingBookmarkClick = listingId => {
    if (!currentUser || !currentUser.attributes) {
      setIsLoginModalOpen(true);
      return;
    }

    const isFavorited = Array.isArray(favoriteListings) && favoriteListings.includes(listingId);
    if (isFavorited) {
      onRemoveFavorite(listingId);
    } else {
      onAddFavorite(listingId);
    }
  };

  const BannerVolumeIcon = props => {
    const { muted } = props;

    return muted ? (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M11 5L6 9H3v6h3l5 4V5z" fill="currentColor" />
        <path d="M16 9l5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M21 9l-5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ) : (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M11 5L6 9H3v6h3l5 4V5z" fill="currentColor" />
        <path
          d="M15.5 8.5c1.6 1.6 1.6 5.4 0 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M18.5 6c3 3 3 9 0 12"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  };

  useEffect(() => {
    const videoEl = bannerVideoRef.current;
    if (!videoEl) {
      return;
    }

    videoEl.muted = isBannerMuted;
    if (!isBannerMuted) {
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  }, [isBannerMuted]);

  const onToggleBannerMute = () => setIsBannerMuted(muted => !muted);

  const getHeadingData = () => {
    let websiteLabel;
    let {
      businessName: title,
      businessBannerImage,
      bannerKey,
      bannerVideoKey,
      bannerYoutubeUrl,
      businessWebsite: website,
      businessPrimaryPhone: phone,
      businessPrimaryEmail: businessEmail, // Try to get business email from publicData if it exists
      team = [],
    } = profileUser?.attributes?.profile?.publicData || {};
    const publicData = profileUser?.attributes?.profile?.publicData || {};
    const address = resolveHeaderCityState(publicData);
    // Use businessEmail if present, otherwise fallback to root email (which might be null for non-authenticated users looking at another user's profile due to Sharetribe privacy rules)
    let email = businessEmail || profileUser?.attributes?.email;

    let picture = storeImageUrl || '/static/footer-logo.png';
    let bannerImage = businessBannerImage || bannerKey;

    let associatedUser;

    let storeTitle = title;

    if (website) {
      websiteLabel = website;
      website = ensureProtocol(website);
    }
    if (isStore) {
      // team = [{...profileUser, isOwner: true}, ...team.map(m => {
      team = [
        ...team
          .map(m => {
            let creator = creators.find(c => m.id && c?.id?.uuid === m.id);
            return { ...creator, isOwner: false };
          })
          .filter(m => m),
      ];

      team = team.map(m => {
        let creatorType = m.isOwner ? OWNER : ASSOCIATED;
        let reviewParam = m.isOwner ? reviewsItems : [];
        let {
          authorObj,
          authorId,
          authorName,
          rating,
          ratingCounter,
          authorType,
          reviews: memberReviews,
        } = prepareMemberData(m, m, creatorType, reviewParam, !m.isOwner);

        return {
          ...authorObj,
          authorId,
          authorName,
          rating,
          ratingCounter,
          authorType,
          reviews: memberReviews,
        };
      });
    }

    if (!isStore) {
      if (isOwner) {
        title = profileUser?.attributes?.profile?.displayName || title;
      } else {
        let memberId = props?.params?.memberId;
        associatedUser = creators.find(u => memberId && u?.id?.uuid === memberId);

        if (associatedUser) {
          title = associatedUser?.attributes?.profile?.displayName || title;
        }
      }
    }

    const aboutText = resolveAboutText({
      isStore,
      isOwner,
      profileUser,
      associatedUser,
      profileBio,
    });

    return {
      picture,
      title,
      storeTitle,
      website,
      websiteLabel,
      phone,
      email,
      associatedUser,
      members: team,
      address,
      bannerImage,
      bannerVideoKey,
      bannerYoutubeUrl,
      aboutText,
    };
  };

  let {
    picture,
    title,
    storeTitle,
    website,
    websiteLabel,
    phone,
    email,
    members,
    address,
    associatedUser,
    bannerImage,
    bannerVideoKey,
    bannerYoutubeUrl,
    aboutText,
  } = getHeadingData();

  // Prepare banner image URL
  const bannerUrl = bannerImage ? `${process.env.REACT_APP_CLOUDFRONT_URL}/${bannerImage}` : null;
  const bannerVideoUrl = bannerVideoKey
    ? `${process.env.REACT_APP_CLOUDFRONT_URL}/${bannerVideoKey}`
    : null;
  const bannerYoutubeVideoId = extractYoutubeVideoId(bannerYoutubeUrl);
  const bannerYoutubeEmbedUrl = bannerYoutubeVideoId
    ? buildYoutubeEmbedUrl(bannerYoutubeVideoId, {
        autoplay: '1',
        mute: isBannerMuted ? '1' : '0',
        playsinline: '1',
        loop: '1',
        playlist: bannerYoutubeVideoId,
        rel: '0',
        controls: '0',
        modestbranding: '1',
      })
    : null;
  const showBanner = isStore;

  if (associatedUser) {
    reviewsItems = associatedUser?.attributes?.profile?.publicData?.reviews || [];
  } else if (isStore) {
    let membersReviews = members.filter(m => !m.isOwner).flatMap(m => m.reviews);
    reviewsItems = [...reviewsItems, ...membersReviews];
  }

  let membersComponent = isStore
    ? members.map((m, m_key) => {
        let { authorId, authorName, rating, ratingCounter, authorType } = m;

        let isMemberAssociated = authorType == ASSOCIATED;
        let profilePage = isMemberAssociated ? 'TeamPageMember' : 'TeamPage';
        let userRouteParams = {
          id: profileUser?.id?.uuid,
          type: isMemberAssociated ? 'member' : 'owner',
          ...(isMemberAssociated ? { memberId: authorId || m?.id?.uuid } : {}),
        };

        return (
          <div className={css.listingCardAuthorContainer} key={m_key}>
            <div className={css.authorPicture}>
              <Avatar user={m} disableProfileLink={true} />
            </div>
            <div className={css.authorAndReviewContainer}>
              <div className={css.authorName}>
                <InlineTextButton
                  onClick={event => inlineListener(event, profilePage, userRouteParams)}
                >
                  {authorName}
                </InlineTextButton>
                {isStore ? `, Sales ${authorType == ASSOCIATED ? 'Associate' : 'Owner'}` : null}
                {!isStore ? ' at ' : null}
                {!isStore ? (
                  <InlineTextButton
                    onClick={event =>
                      inlineListener(event, 'TeamPage', { id: profileUser.id.uuid, type: 'detail' })
                    }
                  >
                    {title || props?.config?.marketplaceName}
                  </InlineTextButton>
                ) : null}
              </div>
              <div className={css.ratingContainer}>
                <div>
                  <ReviewRating rating={rating} reviewStarClassName={css.ratingStar} />
                </div>
                <div className={css.ratingCounter}>({ratingCounter} reviews)</div>
              </div>
            </div>
          </div>
        );
      })
    : null;

  return (
    <>
      {showBanner && (
        <div className={css.bannerContainer}>
          {bannerVideoUrl ? (
            <>
              <video
                ref={bannerVideoRef}
                className={css.bannerImage}
                src={bannerVideoUrl}
                autoPlay
                muted={isBannerMuted}
                loop
                playsInline
                poster={bannerUrl || undefined}
              />
              <button
                type="button"
                className={css.bannerMuteToggle}
                onClick={onToggleBannerMute}
                aria-pressed={!isBannerMuted}
                aria-label={isBannerMuted ? 'Unmute banner video' : 'Mute banner video'}
              >
                <BannerVolumeIcon muted={isBannerMuted} />
              </button>
            </>
          ) : bannerYoutubeEmbedUrl ? (
            <>
              <iframe
                key={bannerYoutubeEmbedUrl}
                src={bannerYoutubeEmbedUrl}
                className={css.bannerEmbed}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                title="Store banner video"
              />
              <button
                type="button"
                className={css.bannerMuteToggle}
                onClick={onToggleBannerMute}
                aria-pressed={!isBannerMuted}
                aria-label={isBannerMuted ? 'Unmute banner video' : 'Mute banner video'}
              >
                <BannerVolumeIcon muted={isBannerMuted} />
              </button>
            </>
          ) : bannerUrl ? (
            <img src={bannerUrl} alt="Banner" className={css.bannerImage} />
          ) : (
            <div className={css.bannerPlaceholder}></div>
          )}
        </div>
      )}
      <div className={classNames(css.profileBg, { [css.profileBgWithBanner]: showBanner })}>
        <div className={css.container}>
          <div className={css.profileHeading}>
            <div className={css.profilePicture}>
              {isStore ? (
                <img src={picture} alt={title || storeTitle || 'Seller'} />
              ) : (
                <AvatarLarge
                  className={css.avatarLarge}
                  user={associatedUser || profileUser}
                  disableProfileLink
                />
              )}
            </div>
            <div className={css.profileInfoCenter}>
              <h1 className={css.profileTitleH1}>{title}</h1>
              {!isStore ? (
                <div className={css.salesAssociateInfo}>
                  <img src={picture} className={css.memberTeamPicture} />
                  {`Sales ${isAssociated ? 'Associate' : 'Owner'} at `}
                  <InlineTextButton
                    onClick={event =>
                      inlineListener(event, 'TeamPage', { id: profileUser.id.uuid, type: 'detail' })
                    }
                  >
                    {storeTitle || props?.config?.marketplaceName}
                  </InlineTextButton>
                </div>
              ) : null}
              {address ? <div className={css.profileLocationH2}>{address}</div> : null}
            </div>
            <div className={css.profileContactRight}>
              {website ? (
                <div className={css.contactRow}>
                  <IconWeb className={css.contactIcon} />
                  <a
                    href={website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={css.contactLink}
                  >
                    {websiteLabel}
                  </a>
                </div>
              ) : null}
              {phone ? (
                <div className={css.contactRow}>
                  <IconPhone className={css.contactIcon} />
                  <a href={`tel:${phone}`} className={css.contactLink}>
                    {phone}
                  </a>
                </div>
              ) : null}
              {email ? (
                <div className={css.contactRow}>
                  <svg
                    className={css.contactIcon}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinelinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <a href={`mailto:${email}`} className={css.contactLink}>
                    {email}
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {aboutText ? (
        <div className={css.aboutSection}>
          <div className={css.container}>
            <h2 className={css.aboutTitle}>
              <FormattedMessage id="TeamPage.aboutHeading" />
            </h2>
            <p className={css.aboutText}>{aboutText}</p>
          </div>
        </div>
      ) : null}

      {displayedReviews.length > 0 ? (
        <div
          className={classNames(css.highlightedReviewsBg, {
            // The desktop -95px pull is calibrated for direct adjacency to the profile
            // header; with the About section in between it would cover the bio text.
            [css.highlightedReviewsBgWithAbout]: !!aboutText,
          })}
        >
          <div className={css.container}>
            <div className={css.highlightedReviewsGrid}>
              {displayedReviews.slice(0, 3).map((review, index) => (
                <CompactReviewCard key={review.id?.uuid || index} review={review} intl={intl} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className={css.reviewsDivider}>
          <div className={css.container}>
            <div className={css.reviewsDividerLine} />
          </div>
        </div>
      )}
      <div
        className={classNames(css.listingsBg, {
          // Same reasoning as highlightedReviewsBgWithAbout: the -95px listings pull
          // must not slide the listings band over the About section.
          [css.listingsBgWithBannerOverlap]:
            showBanner && displayedReviews.length === 0 && !aboutText,
        })}
      >
        <div className={css.container}>
          <SearchPageWithGrid
            hideHeaderAndFooter={true}
            isEmbedded={true}
            embeddedTitle="Listings"
            showSourceToggle={storeType === 'store'}
            showBrandCarousel={storeType === 'store'}
            brands={BRANDS}
            pageName={props.params.memberId ? 'TeamPageMember' : 'TeamPage'}
            pagePathParams={{
              id: props.params.id,
              ...(props.params.type ? { type: props.params.type } : {}),
              ...(props.params.memberId ? { memberId: props.params.memberId } : {}),
            }}
            ListingCardComponent={CompactListingCard}
            getListingCardProps={listing => {
              const listingId = listing?.id?.uuid;
              const isFavorited =
                !!listingId &&
                Array.isArray(favoriteListings) &&
                favoriteListings.includes(listingId);

              return {
                isFavorited,
                onBookmarkClick: handleListingBookmarkClick,
              };
            }}
            showToggleMap={false}
          />
        </div>
      </div>

      <LoginPromptModal
        id="TeamPage.loginPrompt"
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </>
  );
};

const TeamPageComponent = props => {
  const config = useConfiguration();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const {
    scrollingDisabled,
    currentUser,
    userShowError,
    user,
    intl,
    currentCategory,
    categories,
    ...rest
  } = props;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);
  const { displayName } = profileUser?.attributes?.profile || {};

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  // Determine storeType based on the remaining active TeamPage route shapes.
  const storeType =
    props?.params?.type == 'detail'
      ? 'store'
      : !props?.params?.type
      ? 'member'
      : props?.params?.type;

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  }

  useEffect(() => {
    if (profileUser && profileUser?.id?.uuid) {
      redirectToStore({ redirect: true, history, routeConfiguration, id: profileUser.id.uuid });
    }
  }, [history, profileUser, routeConfiguration]);

  useEffect(() => {
    props.handleDomLoaded();
  }, []);

  useEffect(() => {
    if (props.domLoaded && profileUser && !props.firstLoaded) {
      props.handleFirstLoaded();

      // Get creator IDs from listings
      const listingCreatorIds = (props?.listings || [])
        .map(l => l?.attributes?.publicData?.creatorId)
        .filter(l => l);

      // Get team member IDs from profile
      const teamMemberIds = (profileUser?.attributes?.profile?.publicData?.team || [])
        .map(m => m.id)
        .filter(id => id && id.length > 0); // Filter out empty IDs

      // Combine both arrays and remove duplicates
      const allCreatorIds = [...new Set([...listingCreatorIds, ...teamMemberIds])];

      if (allCreatorIds.length > 0) {
        props.handleCreators(allCreatorIds);
      }
    }
  }, [props?.listings, profileUser]);

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'TeamPage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <MainContent
          config={config}
          profileUser={profileUser}
          userShowError={userShowError}
          currentCategory={currentCategory}
          categories={categories}
          storeType={storeType}
          intl={intl}
          {...rest}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

TeamPageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  creators: [],
  queryReviewsError: null,
};

TeamPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser, creators } = state.user;
  const {
    userId,
    userShowError,
    profileBio,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
    categories,
    subcategories,
    currentCategory,
    currentSubcategory,
    fetchingListings,
  } = state.TeamPage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);
  const favoriteListings = getFavoriteListings(state);

  const { domLoaded, firstLoaded } = state.ui;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    profileBio,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
    categories,
    subcategories,
    currentCategory,
    currentSubcategory,
    fetchingListings,
    creators,
    domLoaded,
    firstLoaded,
    favoriteListings,
  };
};

const mapDispatchToProps = dispatch => ({
  handleDomLoaded: () => dispatch(setDOMLoaded()),
  handleFirstLoaded: () => dispatch(setFirstLoaded()),
  handleCreators: creatorsId => dispatch(fetchCreators(creatorsId, true)),
  onAddFavorite: listingId => dispatch(addFavorite(listingId)),
  onRemoveFavorite: listingId => dispatch(removeFavorite(listingId)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const TeamPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl
)(TeamPageComponent);

export default TeamPage;
